import { useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, MapPin, Clock, ZoomIn } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './gallery.css';

const projects = [
  {
    id: 1,
    title: "Modern Office Tower",
    category: "commercial",
    location: "Greater Noida",
    completionDate: "2024",
    src: ["/assets/in4.jpg", "/assets/in5.jpg"],
    description: "25-story sustainable office complex with state-of-the-art facilities and green building certification.",
    tags: ["LEED Certified", "Smart Building", "Corporate"]
  },
  {
    id: 2,
    title: "Luxury Villa",
    category: "residential",
    location: "Noida",
    completionDate: "2024",
    src: ["/assets/villa/villa.jpg","/assets/villa/villa2.jpg","/assets/villa/villa3.jpg"],
    description: "Premium residential development featuring modern amenities and smart home technology.",
    tags: ["Premium", "Smart Home", "Waterfront"]
  },
  {
    id: 3,
    title: "Industrial Complex",
    category: "industrial",
    location: "Gurugram",
    completionDate: "2023",
    src: ["/assets/ind1.jpg", "/assets/ind2.jpg","/assets/i3.jpg","/assets/ind4.jpg","/assets/ind3.jpg","/assets/ind6.jpg","/assets/ind10.jpg","/assets/ind8.jpg"],
    description: "State-of-the-art industrial facility with advanced manufacturing capabilities.",
    tags: ["Industrial", "Manufacturing", "High-Tech"]
  },
  {
    id: 4,
    title: "Interior Design Portfolio",
    category: "interior",
    location: "Delhi NCR",
    completionDate: "2025",
    src: [
      "/assets/interior/int1.jpg", 
      "/assets/interior/int2.jpg", 
      "/assets/interior/int3.jpg", 
      "/assets/interior/int4.jpg", 
      "/assets/interior/int5.jpg",
      "/assets/interior/int6.jpg",
      "/assets/interior/int7.jpg",
      "/assets/interior/int8.jpg",
      "/assets/interior/int9.jpg",
      "/assets/interior/int10.jpg",
      "/assets/interior/int11.jpg",
      "/assets/interior/int12.jpg",
      "/assets/interior/int13.jpg",
      "/assets/interior/int14.jpg",
      "/assets/interior/int15.jpg",
      "/assets/interior/int16.jpg",
      "/assets/interior/int17.jpg",
      "/assets/interior/int18.jpg",
 
    ],
    description: "Comprehensive interior design services covering residential living spaces, luxury bedrooms, and corporate offices. Our portfolio showcases modern minimalist designs, custom furniture solutions, smart home integration, and professional workspace layouts.",
    tags: ["Modern", "Luxury", "Interior Design"]
  }
];

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const filteredProjects = activeCategory === 'all'
    ? projects
    : projects.filter(project => project.category === activeCategory);

  const handlePrevImage = useCallback((e) => {
    e.stopPropagation();
    if (!selectedProject) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? selectedProject.src.length - 1 : prev - 1
    );
  }, [selectedProject]);

  const handleNextImage = useCallback((e) => {
    e.stopPropagation();
    if (!selectedProject) return;
    setCurrentImageIndex((prev) => 
      prev === selectedProject.src.length - 1 ? 0 : prev + 1
    );
  }, [selectedProject]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && selectedProject) {
      setCurrentImageIndex(prev => 
        prev < selectedProject.src.length - 1 ? prev + 1 : 0
      );
    }
    if (isRightSwipe && selectedProject) {
      setCurrentImageIndex(prev => 
        prev > 0 ? prev - 1 : selectedProject.src.length - 1
      );
    }
  };

  return (
    <section id="gallery" className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 sm:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Improved responsive text sizing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12 lg:mb-16"
        >
          <h2 className="heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
            Our Project Gallery
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto px-4">
            Explore our portfolio of exceptional construction projects, showcasing innovation and craftsmanship.
          </p>
        </motion.div>

        {/* Category Filters - Enhanced mobile layout */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 px-2">
          {['all', 'commercial', 'residential', 'industrial', 'interior'].map((category) => (
            <motion.button
              key={category}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(category)}
              className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-sm font-medium transition-all duration-300
                ${activeCategory === category 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow-md'}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </motion.button>
          ))}
        </div>

        {/* Gallery Grid - Improved responsive grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8"
        >
          <AnimatePresence>
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                layoutId={`project-${project.id}`}
                className="group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                onClick={() => {
                  console.log('Opening modal for project:', project.title);
                  console.log('Project images:', project.src);
                  setSelectedProject(project);
                  setCurrentImageIndex(0);
                }}
              >
                <div className="h-64 sm:h-72 lg:h-80 overflow-hidden bg-gray-100 relative">
                  <img
                    src={project.src[0]}
                    alt={project.title}
                    className="w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500 text-sm" style={{display: 'none'}}>
                    Image not found
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3 leading-tight">
                        {project.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-white/90 mb-3 sm:mb-4">
                        <span className="flex items-center gap-1 sm:gap-2 bg-black/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm text-sm">
                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                          {project.location}
                        </span>
                        <span className="flex items-center gap-1 sm:gap-2 bg-black/20 px-2 sm:px-3 py-1 rounded-full backdrop-blur-sm text-sm">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          {project.completionDate}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {project.tags.map((tag, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 sm:px-3 py-1 rounded-full bg-white/20 text-white text-xs sm:text-sm backdrop-blur-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 bg-black/20 p-2 rounded-full backdrop-blur-sm">
                      <ZoomIn className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Modal - Fixed for both desktop and mobile */}
        <AnimatePresence mode="wait">
          {selectedProject && (
            <motion.div
              key="gallery-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
              className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <div
                className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                {/* Close Button - Always visible and working */}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 z-30 p-2 rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-300"
                >
                  <X className="w-6 h-6 text-gray-800" />
                </motion.button>

                <div className="flex flex-col lg:flex-row h-full max-h-[90vh]">
                  {/* Image Section */}
                  <div className="relative lg:w-2/3 bg-gray-900">
                    <div 
                      className="relative h-64 sm:h-80 lg:h-96 xl:h-[500px]"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      <img
                        src={selectedProject.src[currentImageIndex]}
                        alt={selectedProject.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          console.error('Image failed to load:', selectedProject.src[currentImageIndex]);
                          e.target.src = '/assets/logo.svg'; // Fallback image
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', selectedProject.src[currentImageIndex]);
                        }}
                      />
                    </div>

                    {/* Navigation Controls */}
                    <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handlePrevImage}
                        className="p-3 rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-300 pointer-events-auto"
                      >
                        <ChevronLeft className="w-6 h-6 text-gray-800" />
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleNextImage}
                        className="p-3 rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-300 pointer-events-auto"
                      >
                        <ChevronRight className="w-6 h-6 text-gray-800" />
                      </motion.button>
                    </div>

                    {/* Image Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full text-white text-sm backdrop-blur-sm">
                      {currentImageIndex + 1} / {selectedProject.src.length}
                    </div>
                  </div>

                  {/* Details Section */}
                  <div className="lg:w-1/3 p-6 bg-white overflow-y-auto">
                    <h3 className="text-2xl font-bold mb-4">{selectedProject.title}</h3>
                    <p className="text-gray-600 mb-6 text-base leading-relaxed">
                      {selectedProject.description}
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex flex-col gap-3">
                        <span className="flex items-center gap-2 text-gray-700">
                          <MapPin className="w-5 h-5 text-blue-600" />
                          {selectedProject.location}
                        </span>
                        <span className="flex items-center gap-2 text-gray-700">
                          <Clock className="w-5 h-5 text-blue-600" />
                          {selectedProject.completionDate}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {selectedProject.tags.map((tag, idx) => (
                          <span 
                            key={idx}
                            className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Gallery;